import { ForgotPasswordDto } from "@reactive-resume/dto";
import { useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { AuthResponseDto, LoginDto } from "@reactive-resume/dto";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";

import { USER_KEY, RESUMES_KEY } from "@/client/constants/query-keys";
import { axios } from "@/client/libs/axios";
import { useAuthStore } from "@/client/stores/auth";
import { queryClient } from "@/client/libs/query-client";
import { fetchResumes } from "@/client/services/resume/resumes";

export const coachIntegration = async (data: string) => {
  const response = await axios.post<AuthResponseDto, AxiosResponse<AuthResponseDto>>(
    "/auth/coachIntegration",
    { token: data },
  );

  return response.data;
};

export const importUserData = async () => {
  try {
    const response = await axios.post("/auth/importUserData", {});
    return response.data;
  } catch (error) {
    console.error("Error importing user data:", error);
    throw error;
  }
};

export const useCoachIntegration = () => {
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);

  // Helper function to remove cookies
  const removeCookie = (name: string) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}; secure`;
    // Also try without domain for localhost
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  const clearAuthState = () => {
    // window.localStorage.clear();
    window.localStorage.removeItem("auth");

    // Remove Authentication and Refresh cookies
    removeCookie("Authentication");
    removeCookie("Refresh");

    // Clear user state
    setUser(null);
    //  queryClient.setQueryData(["user"], null);
    queryClient.removeQueries({ queryKey: USER_KEY });
  };

  // Where to land after a successful handshake. Defaults to the resume list, but a
  // token carrying a `coachJob` sends the student straight into the optimization
  // flow for that job instead (see pages/auth/coach-integration/page.tsx).
  const redirectRef = useRef<string>("/dashboard/resumes");

  const {
    error,
    isPending: loading,
    mutateAsync: coachIntegrationFn,
  } = useMutation({
    mutationFn: coachIntegration,
    onSuccess: async (data) => {
      if (data.status === "2fa_required") {
        return navigate("/auth/verify-otp");
      }

      // Set user and update query cache
      setUser(data.user);
      queryClient.setQueryData(["user"], data.user);

      // Call importUserData API after successful login
      try {
        await importUserData();
      } catch (error) {
        console.error("Failed to import user data on coach integration:", error);
        // Don't fail the login if import fails, just log the error
      }

      // Prefetch resume data after successful login
      try {
        await queryClient.fetchQuery({
          queryKey: RESUMES_KEY,
          queryFn: fetchResumes,
        });
      } catch (error) {
        console.error("Error prefetching resumes on coach integration login:", error);
        // Don't fail the login if resume fetch fails
      }

      // Navigate to dashboard after successful authentication
      navigate(redirectRef.current, { replace: true });
    },
    onError: (error) => {
      // Clear auth state on error to ensure clean state
      clearAuthState();
    },
  });

  // Wrapper function that clears auth state before making the API call
  const coachIntegrationWithCleanup = useCallback(async (token: string, redirectTo?: string) => {
    if (redirectTo) redirectRef.current = redirectTo;
        clearAuthState();

    try {
      return await coachIntegrationFn(token);
    } catch (err) {
      clearAuthState();
      throw err;
    }
  }, [coachIntegrationFn]);

  return { coachIntegration: coachIntegrationWithCleanup, loading, error };
};
